'use memo'

import { formatCurrency } from '@urbanx/agx-ui-components'
import {
  Deposit,
  Condition,
  ConditionType,
  DayType,
  FormCondition,
} from '@urbanx/agx.campaigns.client'
import { AgxBadge, AgxSpacer } from '@urbanx/agx.ui-components2'
import { useMemo } from 'react'
import { formatDateOnly } from 'utils/date'

const getConditionTypeText = (conditionType: ConditionType | undefined) => {
  switch (conditionType) {
    case ConditionType.Contract:
      return 'contract date'
    case ConditionType.Finance:
      return 'finance being satisfied'
    case ConditionType.Unconditional:
      return 'unconditional date'
    default:
      return ''
  }
}

const isBusinessDays = (dayType: DayType | undefined | null): boolean => {
  return dayType === DayType.BusinessDays
}

const ConditionDateText = ({
  condition,
  isDeposit = false,
  depositAmount,
  specialConditionIdToClauseNumberMap,
}: {
  condition: FormCondition | Deposit
  isDeposit?: boolean
  depositAmount?: number
  specialConditionIdToClauseNumberMap: {
    [key: string]: string
  }
}) => {
  const dueOrPayable = useMemo(
    () => (isDeposit ? 'Payable' : 'Due'),
    [isDeposit]
  )

  const amountPrefix = useMemo(() => {
    if (!isDeposit || depositAmount === undefined) return ''
    return `${formatCurrency(depositAmount, 0)} `
  }, [isDeposit, depositAmount])

  const referenceDateText = useMemo(() => {
    return formatDateOnly(condition.referenceDate) ?? ''
  }, [condition.referenceDate])

  if (condition.condition === Condition.UponSatisfactionOfSpecialCondition) {
    const clauseNumber =
      specialConditionIdToClauseNumberMap[condition.specialConditionId ?? ''] ??
      ''

    const specialConditionText = `special condition ${clauseNumber}`

    if (condition.daysFromDate && condition.daysFromDate > 0) {
      const dayTypeText =
        condition.dayType === DayType.BusinessDays ? 'business ' : ''
      return (
        <>
          {amountPrefix}
          {dueOrPayable} {condition.daysFromDate} {dayTypeText}day(s) from
          satisfaction of {specialConditionText}
        </>
      )
    }

    return (
      <AgxSpacer
        direction="horizontal"
        gap={'3xs'}
        contentJustification="center"
        contentAlignment="center"
      >
        {amountPrefix}
        {dueOrPayable} on
        <AgxBadge
          size="md"
          type="colourfill"
          colour="grey"
          text={`satisfaction of ${specialConditionText}`}
        ></AgxBadge>
      </AgxSpacer>
    )
  } else if (condition.condition === Condition.OnSpecificDate) {
    return (
      <>
        {amountPrefix}
        {dueOrPayable} on {referenceDateText}
      </>
    )
  } else if (
    condition.condition === Condition.NdaysFromDate ||
    condition.condition === Condition.AsPerContract
  ) {
    const dateText = getConditionTypeText(condition?.conditionType)

    // If daysFromDate is set and greater than 0, show "X day(s) from"
    if (condition.daysFromDate && condition.daysFromDate > 0) {
      const dayTypeText =
        condition.dayType === DayType.BusinessDays ? 'business ' : ''
      return (
        <>
          {amountPrefix}
          {dueOrPayable} {condition.daysFromDate} {dayTypeText}day(s) from{' '}
          <AgxBadge size="md" type="colourfill" colour="grey" text={dateText} />
        </>
      )
    }

    // Otherwise, show "on [date]"
    return (
      <>
        {amountPrefix}
        {dueOrPayable} on{' '}
        <AgxBadge size="md" type="colourfill" colour="grey" text={dateText} />
      </>
    )
  } else if (condition.condition === Condition.FinanceBeingSatisfied) {
    if (condition.daysFromDate && condition.daysFromDate > 0) {
      const dayTypeText = isBusinessDays(condition.dayType) ? 'business ' : ''
      return (
        <>
          {amountPrefix}
          {dueOrPayable} {condition.daysFromDate} {dayTypeText}day(s) from{' '}
          <AgxBadge
            size="md"
            type="colourfill"
            colour="grey"
            text="finance being satisfied"
          />
        </>
      )
    }
    return (
      <>
        {amountPrefix}
        {dueOrPayable}{' '}
        <AgxBadge
          size="md"
          type="colourfill"
          colour="grey"
          text="upon finance being satisfied"
        />
      </>
    )
  } else if (condition.condition === Condition.BuildingAndPestBeingSatisfied) {
    if (condition.daysFromDate && condition.daysFromDate > 0) {
      const dayTypeText = isBusinessDays(condition.dayType) ? 'business ' : ''
      return (
        <>
          {amountPrefix}
          {dueOrPayable} {condition.daysFromDate} {dayTypeText}day(s) from{' '}
          <AgxBadge
            size="md"
            type="colourfill"
            colour="grey"
            text="building and pest being satisfied"
          />
        </>
      )
    }
    return (
      <>
        {amountPrefix}
        {dueOrPayable}{' '}
        <AgxBadge
          size="md"
          type="colourfill"
          colour="grey"
          text="upon building and pest being satisfied"
        />
      </>
    )
  } else if (condition.condition === Condition.OnDate) {
    // If referenceDate is set, show the actual date instead of "contract date"
    if (condition.referenceDate) {
      return (
        <>
          {amountPrefix}
          {dueOrPayable} on{' '}
          <AgxBadge
            size="md"
            type="colourfill"
            colour="grey"
            text={referenceDateText}
          />
        </>
      )
    }

    const dateText = getConditionTypeText(condition?.conditionType)

    return (
      <>
        {amountPrefix}
        {dueOrPayable} on{' '}
        <AgxBadge size="md" type="colourfill" colour="grey" text={dateText} />
      </>
    )
  } else if (
    condition.condition === undefined &&
    condition.conditionType !== undefined
  ) {
    // Handle deposits with conditionType but no condition value set
    const dateText = getConditionTypeText(condition?.conditionType)

    return (
      <>
        {amountPrefix}
        {dueOrPayable} on{' '}
        <AgxBadge size="md" type="colourfill" colour="grey" text={dateText} />
      </>
    )
  } else {
    return null
  }
}

export default ConditionDateText
